import { environment } from '../../environments/environment';

/**
 * Same-company project deep links: /temperature?project=<name>
 *
 * Opening the app on a specific project, so one operator can send another a link
 * to the cure they are looking at instead of "open the pane and search for it".
 *
 * SECURITY POSTURE — read before enabling the UI
 * ----------------------------------------------
 * A link is only a POINTER. It is not, and must not be treated as, an
 * authorisation to view anything: whoever follows it still authenticates as
 * themselves, and the data they get back is whatever the SERVER decides they may
 * have. That distinction matters because the data endpoints do not currently
 * scope by company — a name from another company, pasted in by hand, would be
 * served. The deep link does not create that hole and cannot close it, but it
 * would make it convenient, which is why the share UI USED TO stay behind a
 * `shareLinksEnabled` flag.
 *
 * THAT FLAG IS GONE, and the reason it could go is the reason it existed: the
 * data endpoints now carry caller identity and DataServices enforces company
 * scope (prod2 runs with EnforceDataEndpointCompanyScope=true), so a link can
 * no longer be used to reach another company's project. The gate was removed
 * because its precondition was met — not because it stopped mattering. If that
 * server-side enforcement is ever turned off, this UI needs a gate again.
 *
 * What the client CAN enforce, and does:
 *   - the name must appear in the company-scoped list this user was served, so a
 *     link to a project outside their company opens the picker instead of
 *     attempting a fetch
 *   - exact match only, so a partial or crafted name resolves to nothing
 *
 * That is defence in depth on top of the server fix, never a substitute for it.
 */

/** Query parameter carrying the project name. */
export const PROJECT_QUERY_PARAM = 'project';

/** Anything with a project name — ProjectInfo, or a test double. */
export interface NamedProject {
  ProjectName?: string | null;
}

/**
 * Is the share-link UI enabled?
 *
 * Absent config means OFF. The deep link itself keeps working when this is false
 * — an existing link a user already holds still opens — but nothing in the UI
 * hands out new ones until the server-side company scoping ships.
 */
/**
 * Read the project name from resolved query params.
 *
 * Angular has already percent-decoded these, so no decoding happens here; doing
 * it again would corrupt a name that legitimately contains a '%'. Returns null
 * for anything unusable so callers have exactly one "no deep link" case.
 */
export function readProjectParam(
  params: Record<string, unknown> | null | undefined
): string | null {
  const raw = params?.[PROJECT_QUERY_PARAM];
  if (typeof raw !== 'string') {
    // Angular yields an array when a param repeats (?project=a&project=b).
    // Ambiguous by definition — treat it as absent rather than guessing.
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Read the project name straight from a raw query string.
 *
 * The router-independent path, and the one the persistence guard uses.
 * `ActivatedRoute.snapshot.queryParams` is only populated once the router's
 * initial navigation has settled, and the guard runs inside the MSAL account
 * callback — which on a cold load can resolve FIRST. The snapshot was empty at
 * that instant, so the guard saw no link, restore proceeded, and the persisted
 * project overwrote the linked one. `window.location.search` is populated before
 * any Angular code runs and never changes underneath us.
 */
export function projectParamFromSearch(search: string | null | undefined): string | null {
  if (!search) {
    return null;
  }
  const query = search.startsWith('?') ? search.slice(1) : search;
  for (const pair of query.split('&')) {
    const eq = pair.indexOf('=');
    const key = eq < 0 ? pair : pair.slice(0, eq);
    if (decodeURIComponent(key) !== PROJECT_QUERY_PARAM) {
      continue;
    }
    const raw = eq < 0 ? '' : pair.slice(eq + 1);
    try {
      // '+' is a legal encoding of a space in a query string; decodeURIComponent
      // does not handle it, and a name containing one would silently lose it.
      const value = decodeURIComponent(raw.replace(/\+/g, ' ')).trim();
      return value.length > 0 ? value : null;
    } catch {
      // Malformed percent-encoding — treat as absent rather than throwing on load.
      return null;
    }
  }
  return null;
}

/**
 * The project named in the URL when the bundle first loaded.
 *
 * Captured at MODULE EVALUATION, which happens before Angular bootstraps and
 * before the router's first navigation. Component ngOnInit is too late and too
 * rare: MainLayoutComponent is the persistent layout, so it initialises ONCE —
 * on whatever route the session started at — and never again for later
 * navigations. A capture there saw an empty search on a cold start at `/` and
 * latched null for the rest of the session.
 */
const INITIAL_PROJECT_PARAM: string | null = (() => {
  try {
    return projectParamFromSearch(globalThis.location?.search);
  } catch {
    return null;
  }
})();

/** The deep-linked project from the initial URL, if there was one. */
export function initialDeepLinkProject(): string | null {
  return INITIAL_PROJECT_PARAM;
}

/**
 * Find a project by EXACT name in the company-scoped list.
 *
 * Exact rather than case-insensitive or fuzzy: project names are user-supplied
 * and near-duplicates are common ("Unnamed_2026-08-17_09-41-58" differs from its
 * neighbours only in the timestamp), so a loose match could silently open the
 * wrong cure. A miss is a picker; a wrong hit is bad data on screen.
 */
export function findProjectByName<T extends NamedProject>(
  projects: readonly T[] | null | undefined,
  name: string | null | undefined
): T | null {
  if (!name || !projects) {
    return null;
  }
  for (const project of projects) {
    if (project?.ProjectName === name) {
      return project;
    }
  }
  return null;
}

/**
 * Build a shareable absolute URL for a project.
 *
 * `encodeURIComponent` is what makes this safe for the names that actually occur
 * — spaces, '&', '#', '+' all appear in real project names and each one would
 * otherwise truncate or corrupt the parameter.
 */
export function buildProjectLink(
  origin: string,
  path: string,
  projectName: string
): string {
  const cleanOrigin = (origin ?? '').replace(/\/+$/, '');
  const cleanPath = `/${(path ?? '').replace(/^\/+/, '')}`;
  const encoded = encodeURIComponent(projectName ?? '');
  return `${cleanOrigin}${cleanPath}?${PROJECT_QUERY_PARAM}=${encoded}`;
}

/** Why a deep link did not open a project — drives what the UI does next. */
export type DeepLinkOutcome =
  | { status: 'none' }
  | { status: 'opened'; projectName: string }
  | { status: 'not-found'; projectName: string };

/**
 * Resolve a deep link against the list this user was served.
 *
 * Pure, so the decision is testable without a router or an HTTP round trip; the
 * caller performs the side effects.
 */
export function resolveDeepLink<T extends NamedProject>(
  params: Record<string, unknown> | null | undefined,
  projects: readonly T[] | null | undefined
): DeepLinkOutcome {
  const name = readProjectParam(params);
  if (!name) {
    return { status: 'none' };
  }
  return findProjectByName(projects, name)
    ? { status: 'opened', projectName: name }
    : { status: 'not-found', projectName: name };
}
